import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import PropTypes from "prop-types";
import { toast } from "react-toastify";
import { useAuth } from "../../auth/AuthContext";
import { followUpService } from "../../services/followUpService";
import { usePlanFeature } from "../../hooks/usePlanFeature";
import ConfirmDialog from "../../components/ConfirmDialog/ConfirmDialog";
import PlanGate from "./components/PlanGate";
import FollowUpMetrics from "./components/FollowUpMetrics";
import SequenceList from "./components/SequenceList";
import SequenceFormModal from "./components/SequenceFormModal";
import EnrollmentList from "./components/EnrollmentList";
import {
  ENROLLMENTS_PAGE_SIZE,
  TRIGGER_TYPES,
  isCanceledError,
  isPlanLimitError,
} from "./followUpConstants";
import styles from "./FollowUp.module.css";

const TABS = {
  SEQUENCES: "sequences",
  ENROLLMENTS: "enrollments",
};

/**
 * Tela de Follow-up / Cadências Automatizadas (exclusiva do plano Avançado).
 *
 * Wrapper de gate (pré-check via `usePlanFeature("ai_advanced")`) + componente
 * interno com o conteúdo — evita misturar hooks com early-returns. Quando o
 * plano basta, mas a API responde 403 `plan_limit_reached` (ex.: downgrade), o
 * conteúdo também cai no PlanGate (defesa em profundidade).
 */
export default function FollowUp() {
  const { user } = useAuth();
  const { allowed, upgradeUrl } = usePlanFeature("ai_advanced");

  // Aguarda o AuthContext popular o user antes de decidir o gate.
  if (user === null) return null;

  if (!allowed) {
    return (
      <div className={styles.page}>
        <PlanGate upgradeUrl={upgradeUrl} />
      </div>
    );
  }

  return <FollowUpContent upgradeUrl={upgradeUrl} />;
}

function FollowUpContent({ upgradeUrl }) {
  const [activeTab, setActiveTab] = useState(TABS.SEQUENCES);
  const [planBlocked, setPlanBlocked] = useState(false);

  // ─── Métricas ────────────────────────────────────────────────────────
  const [metrics, setMetrics] = useState(null);
  const [metricsLoading, setMetricsLoading] = useState(true);

  // ─── Sequências ──────────────────────────────────────────────────────
  const [sequences, setSequences] = useState([]);
  const [sequencesLoading, setSequencesLoading] = useState(true);
  const sequencesAbortRef = useRef(null);

  // ─── Modal de cadência ───────────────────────────────────────────────
  const [modalOpen, setModalOpen] = useState(false);
  const [modalTrigger, setModalTrigger] = useState(TRIGGER_TYPES.LEAD);
  const [editTarget, setEditTarget] = useState(null);
  const [savingSequence, setSavingSequence] = useState(false);

  // ─── ConfirmDialog genérico (desativar / excluir cadência, cancelar fila) ─
  const [confirm, setConfirm] = useState(null); // { title, message, confirmLabel, variant, onConfirm }

  // ─── Enrollments ─────────────────────────────────────────────────────
  const [enrollments, setEnrollments] = useState([]);
  const [enrollTotal, setEnrollTotal] = useState(0);
  const [enrollPage, setEnrollPage] = useState(1);
  const [enrollLoading, setEnrollLoading] = useState(true);
  const [enrollLoadingMore, setEnrollLoadingMore] = useState(false);
  const [enrollError, setEnrollError] = useState(false);
  const [statusFilter, setStatusFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [busyEnrollmentId, setBusyEnrollmentId] = useState(null);
  const enrollAbortRef = useRef(null);

  // ─── Helper compartilhado de erro de API ──────────────────────────────
  const handleApiError = useCallback((err, fallbackMsg) => {
    if (isPlanLimitError(err)) {
      setPlanBlocked(true);
      return true; // sinaliza que o erro foi tratado como bloqueio de plano
    }
    const apiMsg = err?.response?.data?.error;
    toast.error(typeof apiMsg === "string" ? apiMsg : fallbackMsg);
    return false;
  }, []);

  // ─── Fetch métricas ────────────────────────────────────────────────────
  const fetchMetrics = useCallback(async (signal) => {
    setMetricsLoading(true);
    try {
      const res = await followUpService.getMetrics({ signal });
      setMetrics(res.data ?? null);
    } catch (err) {
      if (isCanceledError(err)) return;
      if (isPlanLimitError(err)) {
        setPlanBlocked(true);
        return;
      }
      // Métricas são não-críticas — falha silenciosa (cards mostram 0).
      setMetrics(null);
    } finally {
      if (!signal?.aborted) setMetricsLoading(false);
    }
  }, []);

  // ─── Fetch sequências ──────────────────────────────────────────────────
  const fetchSequences = useCallback(
    async ({ silent = false } = {}) => {
      sequencesAbortRef.current?.abort();
      const controller = new AbortController();
      sequencesAbortRef.current = controller;
      if (!silent) setSequencesLoading(true);
      try {
        const res = await followUpService.listSequences({
          signal: controller.signal,
        });
        setSequences(res.data?.sequences ?? []);
      } catch (err) {
        if (isCanceledError(err)) return;
        handleApiError(err, "Não foi possível carregar as cadências.");
      } finally {
        if (sequencesAbortRef.current === controller) {
          setSequencesLoading(false);
        }
      }
    },
    [handleApiError],
  );

  // ─── Fetch enrollments (filtros + paginação + AbortController) ──────────
  const fetchEnrollments = useCallback(
    async ({ page = 1, append = false, status = "", type = "" } = {}) => {
      enrollAbortRef.current?.abort();
      const controller = new AbortController();
      enrollAbortRef.current = controller;

      if (append) setEnrollLoadingMore(true);
      else setEnrollLoading(true);
      setEnrollError(false);

      const params = { page, pageSize: ENROLLMENTS_PAGE_SIZE };
      if (status) params.status = status;
      if (type) params.contactType = type;

      try {
        const res = await followUpService.listEnrollments(params, {
          signal: controller.signal,
        });
        const data = res.data?.enrollments ?? [];
        const total = res.data?.total ?? 0;
        setEnrollments((prev) => (append ? [...prev, ...data] : data));
        setEnrollTotal(total);
        setEnrollPage(page);
      } catch (err) {
        if (isCanceledError(err)) return;
        const blocked = handleApiError(
          err,
          "Não foi possível carregar a fila de contatos.",
        );
        if (!blocked && !append) setEnrollError(true);
      } finally {
        if (enrollAbortRef.current === controller) {
          setEnrollLoading(false);
          setEnrollLoadingMore(false);
        }
      }
    },
    [handleApiError],
  );

  // ─── Mount: métricas + sequências + fila ────────────────────────────────
  useEffect(() => {
    const controller = new AbortController();
    fetchMetrics(controller.signal);
    fetchSequences();
    fetchEnrollments({ page: 1, append: false, status: "", type: "" });
    return () => {
      controller.abort();
      sequencesAbortRef.current?.abort();
      enrollAbortRef.current?.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ─── Métricas cruzadas por sequência (sequenceId → {total, converted, rate}) ─
  const metricsBySequence = useMemo(() => {
    const map = {};
    for (const m of metrics?.sequenceMetrics ?? []) {
      map[m.sequenceId] = {
        total: m.total ?? 0,
        converted: m.converted ?? 0,
        conversionRate: m.conversionRate ?? 0,
      };
    }
    return map;
  }, [metrics]);

  // Mapa id-da-cadência → total de steps, para os progress dots da fila.
  const stepCountBySequence = useMemo(() => {
    const map = {};
    for (const s of sequences) {
      if (s?.id) map[s.id] = Array.isArray(s.steps) ? s.steps.length : 0;
    }
    return map;
  }, [sequences]);

  // ─── Handlers de cadência ───────────────────────────────────────────────
  const openCreate = useCallback((triggerType) => {
    setEditTarget(null);
    setModalTrigger(triggerType);
    setModalOpen(true);
  }, []);

  const openEdit = useCallback((sequence) => {
    setEditTarget(sequence);
    setModalTrigger(sequence.triggerType);
    setModalOpen(true);
  }, []);

  const closeModal = useCallback(() => {
    if (savingSequence) return;
    setModalOpen(false);
    setEditTarget(null);
  }, [savingSequence]);

  const handleSubmitSequence = useCallback(
    async (payload) => {
      setSavingSequence(true);
      try {
        if (editTarget) {
          // Edição: envia name, isDefault e o array COMPLETO de steps
          // (o backend substitui os steps integralmente).
          await followUpService.updateSequence(editTarget.id, {
            name: payload.name,
            isDefault: payload.isDefault,
            steps: payload.steps,
          });
          toast.success("Cadência atualizada! 🎉");
        } else {
          await followUpService.createSequence(payload);
          toast.success("Cadência criada! 🎉");
        }
        setModalOpen(false);
        setEditTarget(null);
        await Promise.all([fetchSequences({ silent: true }), fetchMetrics()]);
      } catch (err) {
        handleApiError(err, "Não foi possível salvar a cadência.");
      } finally {
        setSavingSequence(false);
      }
    },
    [editTarget, fetchSequences, fetchMetrics, handleApiError],
  );

  const handleSetDefault = useCallback(
    async (sequence) => {
      try {
        await followUpService.updateSequence(sequence.id, { isDefault: true });
        toast.success(`"${sequence.name}" agora é a cadência principal.`);
        fetchSequences({ silent: true });
      } catch (err) {
        handleApiError(err, "Não foi possível definir a cadência principal.");
      }
    },
    [fetchSequences, handleApiError],
  );

  const requestDeactivate = useCallback(
    (sequence) => {
      setConfirm({
        title: "Desativar esta cadência?",
        message:
          "Ela deixa de ser usada para novos contatos. Quem já está na fila não é afetado. Você pode reativá-la depois.",
        confirmLabel: "Desativar",
        variant: "danger",
        onConfirm: async () => {
          try {
            await followUpService.deactivateSequence(sequence.id);
            toast.success("Cadência desativada.");
            fetchSequences({ silent: true });
          } catch (err) {
            handleApiError(err, "Não foi possível desativar a cadência.");
          }
        },
      });
    },
    [fetchSequences, handleApiError],
  );

  const handleReactivate = useCallback(
    async (sequence) => {
      try {
        // Reativar NÃO rouba o padrão de outra sequência: só liga isActive.
        await followUpService.updateSequence(sequence.id, { isActive: true });
        toast.success("Cadência reativada.");
        fetchSequences({ silent: true });
      } catch (err) {
        handleApiError(err, "Não foi possível reativar a cadência.");
      }
    },
    [fetchSequences, handleApiError],
  );

  const requestDelete = useCallback(
    (sequence) => {
      setConfirm({
        title: "Excluir esta cadência?",
        message:
          "A cadência será removida da sua lista e não enviará mais mensagens. Esta ação não pode ser desfeita pela tela.",
        confirmLabel: "Excluir",
        variant: "danger",
        onConfirm: async () => {
          try {
            // Backend faz soft delete (isActive: false) na rota DELETE.
            await followUpService.deactivateSequence(sequence.id);
            toast.success("Cadência excluída.");
            fetchSequences({ silent: true });
          } catch (err) {
            handleApiError(err, "Não foi possível excluir a cadência.");
          }
        },
      });
    },
    [fetchSequences, handleApiError],
  );

  // ─── Handlers de fila ───────────────────────────────────────────────────
  const handleStatusFilter = useCallback(
    (status) => {
      if (status === statusFilter) return;
      setStatusFilter(status);
      fetchEnrollments({ page: 1, append: false, status, type: typeFilter });
    },
    [statusFilter, typeFilter, fetchEnrollments],
  );

  const handleTypeFilter = useCallback(
    (type) => {
      if (type === typeFilter) return;
      setTypeFilter(type);
      fetchEnrollments({ page: 1, append: false, status: statusFilter, type });
    },
    [typeFilter, statusFilter, fetchEnrollments],
  );

  const handleLoadMore = useCallback(() => {
    fetchEnrollments({
      page: enrollPage + 1,
      append: true,
      status: statusFilter,
      type: typeFilter,
    });
  }, [enrollPage, statusFilter, typeFilter, fetchEnrollments]);

  const handleRetryEnrollments = useCallback(() => {
    fetchEnrollments({
      page: 1,
      append: false,
      status: statusFilter,
      type: typeFilter,
    });
  }, [statusFilter, typeFilter, fetchEnrollments]);

  // Retomar — otimista (status volta a "active"), rollback em erro.
  const handleResume = useCallback(
    async (enrollment) => {
      const prev = enrollment.status;
      setBusyEnrollmentId(enrollment.id);
      setEnrollments((list) =>
        list.map((e) =>
          e.id === enrollment.id ? { ...e, status: "active" } : e,
        ),
      );
      try {
        await followUpService.resumeEnrollment(enrollment.id);
        toast.success("Mensagens retomadas para este contato.");
        fetchMetrics();
      } catch (err) {
        setEnrollments((list) =>
          list.map((e) =>
            e.id === enrollment.id ? { ...e, status: prev } : e,
          ),
        );
        handleApiError(err, "Não foi possível retomar este contato.");
      } finally {
        setBusyEnrollmentId(null);
      }
    },
    [fetchMetrics, handleApiError],
  );

  // Cancelar — ConfirmDialog + otimista (status → opted_out), rollback em erro.
  const requestCancel = useCallback(
    (enrollment) => {
      setConfirm({
        title: "Cancelar este contato?",
        message:
          "Ao cancelar, este contato não receberá mais mensagens automáticas desta cadência.",
        confirmLabel: "Sim, cancelar",
        variant: "danger",
        onConfirm: async () => {
          const prev = enrollment.status;
          setBusyEnrollmentId(enrollment.id);
          setEnrollments((list) =>
            list.map((e) =>
              e.id === enrollment.id ? { ...e, status: "opted_out" } : e,
            ),
          );
          try {
            await followUpService.cancelEnrollment(enrollment.id);
            toast.success("Contato removido da fila.");
            fetchMetrics();
          } catch (err) {
            setEnrollments((list) =>
              list.map((e) =>
                e.id === enrollment.id ? { ...e, status: prev } : e,
              ),
            );
            handleApiError(err, "Não foi possível cancelar este contato.");
          } finally {
            setBusyEnrollmentId(null);
          }
        },
      });
    },
    [fetchMetrics, handleApiError],
  );

  const closeConfirm = useCallback(() => setConfirm(null), []);

  const runConfirm = useCallback(async () => {
    const action = confirm?.onConfirm;
    setConfirm(null);
    if (action) await action();
  }, [confirm]);

  // ─── Plano bloqueado em runtime (403 vindo de qualquer chamada) ─────────
  if (planBlocked) {
    return (
      <div className={styles.page}>
        <PlanGate upgradeUrl={upgradeUrl} />
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <h1 className={styles.title}>
          <span aria-hidden="true">📨</span> Follow-up automático
        </h1>
        <p className={styles.subtitle}>
          Fale automaticamente com clientes que sumiram. O sistema envia
          mensagens suas, uma após a outra, até eles voltarem ou desistirem.
        </p>
      </header>

      <FollowUpMetrics
        byStatus={metrics?.enrollmentsByStatus ?? null}
        loading={metricsLoading}
      />

      <nav
        className={styles.tabs}
        role="tablist"
        aria-label="Seções de Follow-up"
      >
        <button
          type="button"
          role="tab"
          id="tab-sequences"
          aria-selected={activeTab === TABS.SEQUENCES}
          aria-controls="panel-sequences"
          className={`${styles.tab} ${activeTab === TABS.SEQUENCES ? styles.tabActive : ""}`.trim()}
          onClick={() => setActiveTab(TABS.SEQUENCES)}
        >
          Minhas Cadências
        </button>
        <button
          type="button"
          role="tab"
          id="tab-enrollments"
          aria-selected={activeTab === TABS.ENROLLMENTS}
          aria-controls="panel-enrollments"
          className={`${styles.tab} ${activeTab === TABS.ENROLLMENTS ? styles.tabActive : ""}`.trim()}
          onClick={() => setActiveTab(TABS.ENROLLMENTS)}
        >
          Quem está na fila
        </button>
      </nav>

      {activeTab === TABS.SEQUENCES && (
        <section
          id="panel-sequences"
          role="tabpanel"
          aria-labelledby="tab-sequences"
        >
          <SequenceList
            sequences={sequences}
            metricsBySequence={metricsBySequence}
            loading={sequencesLoading}
            onCreate={openCreate}
            onEdit={openEdit}
            onSetDefault={handleSetDefault}
            onDeactivate={requestDeactivate}
            onReactivate={handleReactivate}
            onDelete={requestDelete}
          />
        </section>
      )}

      {activeTab === TABS.ENROLLMENTS && (
        <section
          id="panel-enrollments"
          role="tabpanel"
          aria-labelledby="tab-enrollments"
        >
          <EnrollmentList
            enrollments={enrollments}
            total={enrollTotal}
            loading={enrollLoading}
            loadingMore={enrollLoadingMore}
            error={enrollError}
            statusFilter={statusFilter}
            typeFilter={typeFilter}
            stepCountBySequence={stepCountBySequence}
            busyId={busyEnrollmentId}
            onStatusChange={handleStatusFilter}
            onTypeChange={handleTypeFilter}
            onLoadMore={handleLoadMore}
            onResume={handleResume}
            onCancel={requestCancel}
            onRetry={handleRetryEnrollments}
          />
        </section>
      )}

      {modalOpen && (
        <SequenceFormModal
          isOpen={modalOpen}
          triggerType={modalTrigger}
          sequence={editTarget}
          submitting={savingSequence}
          onSubmit={handleSubmitSequence}
          onClose={closeModal}
        />
      )}

      <ConfirmDialog
        isOpen={Boolean(confirm)}
        title={confirm?.title}
        message={confirm?.message}
        confirmLabel={confirm?.confirmLabel}
        confirmVariant={confirm?.variant ?? "primary"}
        onConfirm={runConfirm}
        onCancel={closeConfirm}
      />
    </div>
  );
}

FollowUpContent.propTypes = {
  upgradeUrl: PropTypes.string.isRequired,
};
